import React, {useState, useEffect, useRef} from 'react';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {Platform, StatusBar} from 'react-native';
import TextGeneric from '../../../components/textGeneric';
import GenericButton from '../../../components/button';
import {Theme} from '../../../theme/lightTheme';
import MainLayout from '../../mainLayout';
import Animated, {FadeIn} from 'react-native-reanimated';
import {tr} from '../../../translations';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {resendVerification} from '../login/actions';
import {ScreenProps} from '../../../interfaces/screens/mainScreen';
import screens from '../../../config/screens';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {ToastType} from '../../../utils/enums';
import {showGlobalToast} from '../../../connected-components/toast/actions';
import {useRoute, RouteProp} from '@react-navigation/native';

type VerificationPendingRouteParams = {
  verificationPendingPage: {
    email?: string;
    message?: string;
  };
};

const Container = styled.View<{height: number; paddingTop: number}>`
  width: 100%;
  height: ${({height}) => height}px;
  padding-top: ${({paddingTop}) => paddingTop}px;
`;

const ContentContainer = styled.View`
  width: 100%;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  padding-horizontal: 5%;
`;

const ImageContainer = styled(Animated.View)<{width: number}>`
  width: ${({width}) => width}px;
  aspect-ratio: 1.333;
  align-items: center;
  justify-content: center;
  align-self: center;
  overflow: hidden;
`;

const Image = styled.Image`
  width: 100%;
  height: 100%;
  ${Platform.OS !== 'web' && 'resize-mode: contain;'}
`;

const Title = styled(TextGeneric)<{
  color: string;
  fontSize: number;
  paddingBottom: number;
}>`
  color: ${({color}) => color};
  font-size: ${({fontSize}) => fontSize}px;
  text-align: center;
  padding-bottom: ${({paddingBottom}) => paddingBottom}px;
`;

const MessageContainer = styled(Animated.View)<{width: string}>`
  width: ${({width}) => width};
  align-items: center;
  padding-horizontal: 5%;
  flex: 1;
  justify-content: center;
`;

const MessageText = styled(TextGeneric)<{fontSize: number; lineHeight: number}>`
  font-size: ${({fontSize}) => fontSize}px;
  color: ${({theme}) => theme.text.title};
  text-align: center;
  line-height: ${({lineHeight}) => lineHeight}px;
  margin-bottom: ${({theme}) => theme.text.s8}px;
`;

const MessageContainerInner = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
`;

const MessageTextPart = styled(TextGeneric)<{fontSize: number}>`
  font-size: ${({fontSize}) => fontSize}px;
  color: ${({theme}) => theme.text.title};
  text-align: center;
`;

const ResendLink = styled.TouchableOpacity`
  margin-left: 4px;
`;

const ResendLinkText = styled(TextGeneric)<{fontSize: number; color: string}>`
  font-size: ${({fontSize}) => fontSize}px;
  color: ${({color}) => color};
  text-decoration: underline;
  font-weight: bold;
`;

const EmailText = styled(TextGeneric)<{fontSize: number}>`
  font-size: ${({fontSize}) => fontSize}px;
  color: ${({theme}) => theme.primary};
  font-weight: bold;
  margin-top: ${({theme}) => theme.text.s8}px;
`;

const ButtonContainer = styled.View<{width: string}>`
  width: ${({width}) => width};
  align-items: center;
  padding-bottom: 5%;
  padding-top: ${({theme}) => theme.text.s55}px;
`;

const ResendButton = styled.TouchableOpacity<{
  backgroundColor: string;
  disabled: boolean;
}>`
  background-color: ${({backgroundColor, disabled}) =>
    disabled ? backgroundColor + '80' : backgroundColor};
  padding: ${({theme}) => theme.text.s9}px ${({theme}) => theme.text.s8}px;
  border-radius: 6px;
  align-items: center;
  justify-content: center;
  min-width: 120px;
  opacity: ${({disabled}) => (disabled ? 0.6 : 1)};
`;

const ResendButtonText = styled(TextGeneric)<{color: string; fontSize: number}>`
  color: ${({color}) => color};
  font-size: ${({fontSize}) => fontSize}px;
  text-align: center;
`;

const BackToLoginLink = styled.TouchableOpacity`
  align-items: center;
  padding-top: ${({theme}) => theme.text.s8}px;
  width: 100%;
`;

const LinkText = styled(TextGeneric)<{fontSize: number; color: string}>`
  font-size: ${({fontSize}) => fontSize}px;
  color: ${({color}) => color};
  text-decoration: underline;
  text-align: center;
`;

/**
 * Masks an email address for display (e.g., j***@gmail.com)
 */
const maskEmail = (email: string): string => {
  if (!email) return '';
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return email;
  const maskedLocal = localPart.length > 1 
    ? `${localPart[0]}${'*'.repeat(Math.min(localPart.length - 1, 3))}`
    : localPart;
  return `${maskedLocal}@${domain}`;
};

/**
 * Verification Pending screen component.
 */
const VerificationPending: React.FC<ScreenProps> = ({navigation}) => {
  const theme = useTheme() as Theme;
  const {bottom: bottomInset, top: topInset} = useSafeAreaInsets();
  const {hdp, wdp, getByScreenSize, isLargeScreen} = useResponsiveDimensions();
  
  const route = useRoute<RouteProp<VerificationPendingRouteParams, 'verificationPendingPage'>>();
  const email = route?.params?.email || '';
  const initialMessage = route?.params?.message || tr('register.verificationRequired');
  const [message] = useState(initialMessage);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [clickCount, setClickCount] = useState(0);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Base time: 3 minutes (180 seconds), increases by 1 minute (60 seconds) each click
  const getWaitTime = (clicks: number): number => {
    return 180 + clicks * 60; // 3 min + (clicks * 1 min)
  };

  // Format seconds to MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Countdown timer effect
  useEffect(() => {
    if (countdown <= 0) {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      return;
    }

    // Clear any existing interval
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // Start new interval
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [countdown]);

  const handleResend = async () => {
    if (!email || isResending || countdown > 0) return;
    
    setIsResending(true);
    const success = await resendVerification(email);
    setIsResending(false);
    
    if (success) {
      showGlobalToast({
        message: tr('register.verificationEmailSent'),
        type: ToastType.SUCCESS,
      });
      
      // Increase click count and set new countdown
      const newClickCount = clickCount + 1;
      setClickCount(newClickCount);
      const waitTime = getWaitTime(newClickCount);
      setCountdown(waitTime);
    }
  };

  const handleBackToLogin = () => {
    navigation?.navigate(screens.auth.login);
  };

  // Calculate responsive values
  const containerHeight =
    hdp(100) - ((StatusBar.currentHeight || 0) + Math.max(bottomInset, hdp(2)));
  const imageWidth = wdp(getByScreenSize(45, 13.5, 40));
  const contentWidth = isLargeScreen ? '50%' : '100%';
  const buttonWidth = wdp(isLargeScreen ? 25 : 85);
  const messageWidth = isLargeScreen ? '80%' : '100%';

  return (
    <MainLayout
      isCustomHeader
      showOverlay={false}
      noPaddingHeader
      keyboardAvoidScrollView>
      <Container
        height={containerHeight}
        paddingTop={Math.max(topInset, hdp(5))}>
        <Title
          color={theme.text.dark}
          fontSize={theme.text.s4}
          paddingBottom={hdp(3)}>
          {tr('register.verificationPendingTitle', 'Email Verification Required')}
        </Title>
        <ContentContainer>
          <ImageContainer entering={FadeIn} width={imageWidth}>
            <Image
              resizeMode={'contain'}
              source={require('../../../assets/images/onboarding/onboard-1.png')}
            />
          </ImageContainer>
          <MessageContainer 
            entering={FadeIn.delay(200)} 
            width={messageWidth}>
            <MessageText
              fontSize={theme.text.s7}
              lineHeight={theme.text.s7 * 1.5}>
              {message}
            </MessageText>
            {email && (
              <EmailText fontSize={theme.text.s8}>
                {maskEmail(email)}
              </EmailText>
            )}
          </MessageContainer>
          <ButtonContainer width={contentWidth}>
            <BackToLoginLink onPress={handleBackToLogin}>
              <LinkText
                fontSize={theme.text.s8}
                color={theme.primary}>
                {tr('register.backToLogin', 'Back to Login')}
              </LinkText>
            </BackToLoginLink>
            <MessageContainerInner style={{marginTop: theme.text.s8}}>
              <MessageTextPart fontSize={theme.text.s8}>
                {tr('register.didntReceiveEmail', "Didn't receive the email?")}{' '}
              </MessageTextPart>
              <ResendLink
                onPress={handleResend}
                disabled={isResending || !email || countdown > 0}>
                <ResendLinkText
                  fontSize={theme.text.s8}
                  color={countdown > 0 ? theme.text.title : theme.primary}>
                  {countdown > 0
                    ? `${tr('register.resendIn', 'Resend in')} ${formatTime(countdown)}`
                    : tr('register.resend', 'Resend')}
                </ResendLinkText>
              </ResendLink>
            </MessageContainerInner>
          </ButtonContainer>
        </ContentContainer>
      </Container>
    </MainLayout>
  );
};

export default VerificationPending;

